// @flow

import ReactDOM from 'react-dom';
import React, { Component, type ElementRef } from 'react';

import HelpButton from 'app/common/components/HelpButton/HelpButton';
import TeamsGuideModal from 'app/team-guide/components/TeamsGuideModal';
import { ViewTypes } from 'app/team-guide/utils/FAQ';
import { GUIDE_VIEW, WELCOME_VIEW } from 'app/team-guide/utils/constants';

type TeamsGuideAppProps = {
  view: $Values<typeof ViewTypes>,
  modalStyle: {},
  profileId: string,
  isModalOpen: boolean,
  shouldRenderWelcomeModal: boolean,
  onClose: () => void,
  onToggleModal: () => void,
  onSelectView: (view: string) => void,
  onWelcomeModalClose: (profileId: string) => void,
  setButtonRef: (ref: ElementRef<any>) => void,
  setModalRef: (ref: ElementRef<any>) => void,
};

type TeamsGuideAppState = {
  view: $Values<typeof ViewTypes>,
  isModalOpen: boolean,
  modalStyle: {},
};

class TeamsGuideAppStateful extends Component<
  TeamsGuideAppProps,
  TeamsGuideAppState
> {
  constructor(props: TeamsGuideAppProps) {
    super(props);

    this.state = this.initialState;
  }

  initialState = {
    view: GUIDE_VIEW,
    isModalOpen: false,
    modalStyle: {},
  };

  buttonRef: ElementRef<any>;
  modalRef: ElementRef<any>;

  UNSAFE_componentWillReceiveProps(nextProps: TeamsGuideAppProps) {
    if (
      nextProps.shouldRenderWelcomeModal !== this.props.shouldRenderWelcomeModal
    ) {
      if (nextProps.shouldRenderWelcomeModal) {
        this.setState({
          view: WELCOME_VIEW,
          isModalOpen: true,
        });
      }
    }
  }

  onToggleModal = () => this.setState({ isModalOpen: !this.state.isModalOpen });

  resetInitialState = () => {
    this.setState(this.initialState);
  };

  closeModalWithTransition = () => {
    // If both refs don't exist then we can't calculate the
    // distance between them in order to transition them, so bail.
    if (!(this.buttonRef && this.modalRef)) {
      return this.resetInitialState();
    }

    // The actual button DOM node
    const buttonNode = ReactDOM.findDOMNode(this.buttonRef);
    // The DOM node for the _overlay_ since that's what `Modal` renders
    const overlayNode = ReactDOM.findDOMNode(this.modalRef);
    // This is terrible, but grab the `firstChild` of the overlay
    // which is the modal itself.
    const modalNode = overlayNode ? overlayNode.firstChild : null;

    // If the refs exist but don't return any nodes then we can't
    // calculate the distance either, so bail.
    if (!(buttonNode && modalNode)) {
      return this.resetInitialState();
    }

    // $FlowFixMe
    const buttonRect = buttonNode.getBoundingClientRect();
    // $FlowFixMe
    const modalRect = modalNode.getBoundingClientRect();
    // Midpoint of button within viewport
    const buttonMid = {
      x: buttonRect.left + buttonRect.width / 2,
      y: buttonRect.top + buttonRect.height / 2,
    };
    // Midpoint of modal within viewport
    const modalMid = {
      x: modalRect.left + modalRect.width / 2,
      y: modalRect.top + modalRect.height / 2,
    };
    // Distance between button's middle and modal's middle
    const x = buttonMid.x - modalMid.x;
    const y = buttonMid.y - modalMid.y;
    // Duration of transition
    const duration = x && y ? 500 : 0;
    // Start transition only if we've calculated a distance.
    if (x && y) {
      this.setState({
        modalStyle: {
          // - `translate3d` for performance.
          // - Keep 50% translation so modal doesn't jerk around.
          // - Translate by `x` and `y` to move the modal's center
          //   to the button's center.
          // - Scale to non-zero value for proper positioning
          transform: `translate3d(-50%, -50%, 0) translate3d(${x}px, ${y}px, 0) scale(0.05)`,
          opacity: 0,
          transition: `all ${duration}ms ease-in-out`,
          willChange: 'transform',
        },
      });
    }
    // Regardless of whether we transitioned, this should fire,
    // `duration` just might be `0`. This is a simple, if semi-inaccurate
    // way to close the modal after the transition completes.
    return setTimeout(this.resetInitialState, duration);
  };

  onClose = () => {
    const { view } = this.state;
    const { onWelcomeModalClose, profileId } = this.props;

    if (view === WELCOME_VIEW) {
      onWelcomeModalClose(profileId);
    }

    this.closeModalWithTransition();
  };

  setButtonRef = (ref: ElementRef<any>) => {
    this.buttonRef = ref;
  };

  setModalRef = (ref: ElementRef<any>) => {
    this.modalRef = ref;
  };

  onSelectView = (view: string) => {
    if (this.state.view === WELCOME_VIEW) {
      const { onWelcomeModalClose, profileId } = this.props;

      onWelcomeModalClose(profileId);
    }

    this.setState({
      view,
      isModalOpen: true,
    });
  };

  render() {
    return (
      <TeamsGuideApp
        {...this.props}
        {...this.state}
        onClose={this.onClose}
        onSelectView={this.onSelectView}
        onToggleModal={this.onToggleModal}
        setButtonRef={this.setButtonRef}
        setModalRef={this.setModalRef}
      />
    );
  }
}

const TeamsGuideApp = ({
  view,
  isModalOpen,
  modalStyle,
  setButtonRef,
  setModalRef,
  onToggleModal,
  onSelectView,
  onClose,
}: TeamsGuideAppProps) => (
  <div>
    <HelpButton
      children="Team Guide"
      icon="team"
      kind="team"
      onClick={onToggleModal}
      ref={setButtonRef}
    />
    {isModalOpen && (
      <TeamsGuideModal
        onClose={onClose}
        onSelectView={onSelectView}
        ref={setModalRef}
        style={modalStyle}
        view={view}
      />
    )}
  </div>
);

export default TeamsGuideAppStateful;
